import type { SlackMessage } from '../../../api/slackConversation';
import type { ExcerptMapping } from './ConversationView';

/** cxItemId can be string or MongoDB-style { $oid: string } */
export type CxItemIdRef = string | { $oid?: string };

export interface EvidenceExcerpt {
  excerpt: string;
  excerptFirstChar?: number;
  excerptLastChar?: number;
  entity?: string;
  /** Which cx item (e.g. which day) this excerpt belongs to */
  cxItemId?: CxItemIdRef;
}

/**
 * Normalize text for matching (collapse whitespace, trim).
 */
function normalizeForMatch(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Find message ts that contain the excerpt text (in message body or in replies).
 */
function findMessageTsContainingExcerpt(
  excerptText: string,
  messages: SlackMessage[]
): string[] {
  const normalizedExcerpt = normalizeForMatch(excerptText);
  if (!normalizedExcerpt) return [];
  const result: string[] = [];
  for (const msg of messages) {
    const msgText = normalizeForMatch(msg.text || '');
    if (msgText.includes(normalizedExcerpt)) {
      result.push(msg.ts);
    }
    for (const reply of msg.replies || []) {
      const replyText = normalizeForMatch(reply.text || '');
      if (replyText.includes(normalizedExcerpt)) {
        result.push(reply.ts);
      }
    }
  }
  return result;
}

/**
 * Build excerpt mapping from evidence excerpts and all loaded days' messages.
 * Returns which message ts to highlight and which message ts gets which ref index for scrolling.
 * Caller must pass excerptRefsArray and attach it to the returned mapping.
 */
export function buildExcerptMapping(
  excerpts: EvidenceExcerpt[],
  days: Array<{ date: string; messages: SlackMessage[] }>,
  excerptRefsArray: React.MutableRefObject<(HTMLDivElement | null)[]>
): ExcerptMapping {
  const excerptMessageTsHighlight = new Set<string>();
  const excerptRefIndexByMessageTs = new Map<string, number>();
  const excerptTextsByMessageTs = new Map<string, string[]>();

  excerpts.forEach((excerpt, index) => {
    const excerptText = excerpt.excerpt || '';
    if (!excerptText) return;
    let firstMessageTs: string | null = null;
    for (const day of days) {
      const messageTsList = findMessageTsContainingExcerpt(excerptText, day.messages);
      for (const ts of messageTsList) {
        excerptMessageTsHighlight.add(ts);
        if (!excerptTextsByMessageTs.has(ts)) excerptTextsByMessageTs.set(ts, []);
        const list = excerptTextsByMessageTs.get(ts);
        if (list && !list.includes(excerptText)) list.push(excerptText);
        if (firstMessageTs == null) firstMessageTs = ts;
      }
      if (firstMessageTs != null) break;
    }
    if (firstMessageTs != null) {
      excerptRefIndexByMessageTs.set(firstMessageTs, index);
    }
  });

  return {
    excerptMessageTsHighlight,
    excerptRefIndexByMessageTs,
    excerptTextsByMessageTs,
    excerptRefsArray,
  };
}
